"use client";

import type { SellListingMode } from "@/lib/listing/bulkListingTypes";
import {
  BULK_LISTING_MAX_ITEMS,
  BULK_LISTING_MIN_ITEMS,
} from "@/lib/listing/bulkListingTypes";

export interface BulkListingModeToggleProps {
  mode: SellListingMode;
  onChange: (mode: SellListingMode) => void;
  disabled?: boolean;
}

export default function BulkListingModeToggle({
  mode,
  onChange,
  disabled = false,
}: BulkListingModeToggleProps) {
  return (
    <div
      className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between rounded-lg border border-border bg-surface p-4"
      role="group"
      aria-label="Listing mode"
      data-onboarding="bulk-mode-toggle"
    >
      <div>
        <span className="text-sm font-medium text-foreground">
          How do you want to list?
        </span>
        <p className="text-xs text-text-light mt-0.5">
          <span className="sm:hidden">
            One item, or {BULK_LISTING_MIN_ITEMS}–{BULK_LISTING_MAX_ITEMS} with
            shared pickup.
          </span>
          <span className="hidden sm:inline">
            Single walks you through one item. Several items reuses location and
            pickup for {BULK_LISTING_MIN_ITEMS}–{BULK_LISTING_MAX_ITEMS} items
            in one batch.
          </span>
        </p>
      </div>
      <div className="inline-flex rounded-lg border border-border p-0.5 bg-secondary shrink-0">
        <button
          type="button"
          disabled={disabled}
          onClick={() => onChange("single")}
          className={`rounded-md px-4 py-2 text-sm font-medium transition-colors ${
            mode === "single"
              ? "bg-surface text-foreground shadow-sm"
              : "text-text-light hover:text-foreground"
          }`}
        >
          One item
        </button>
        <button
          type="button"
          disabled={disabled}
          onClick={() => onChange("bulk")}
          className={`rounded-md px-4 py-2 text-sm font-medium transition-colors ${
            mode === "bulk"
              ? "bg-surface text-foreground shadow-sm"
              : "text-text-light hover:text-foreground"
          }`}
        >
          Several items ({BULK_LISTING_MIN_ITEMS}–{BULK_LISTING_MAX_ITEMS})
        </button>
      </div>
    </div>
  );
}
